import { useState, useEffect, useCallback } from 'react'
import { api } from '../../lib/api'
import { useToast } from '../ui/Toast'
import { Button } from '../ui/Button'

interface Props {
  siteId: string
  onClose: () => void
  onInstalled: () => void
}

function XIcon({ size = 16, style }: { size?: number; style?: React.CSSProperties }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={style}><path d="M18 6L6 18"/><path d="M6 6l12 12"/></svg>
}

function DownloadIcon({ size = 14, style }: { size?: number; style?: React.CSSProperties }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={style}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
}

function CopyIcon({ size = 14, style }: { size?: number; style?: React.CSSProperties }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={style}><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
}

function CheckIcon({ size = 14, style }: { size?: number; style?: React.CSSProperties }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" style={style}><polyline points="20 6 9 17 4 12"/></svg>
}

export function AgentInstallModal({ siteId, onClose, onInstalled }: Props) {
  const [agentType, setAgentType] = useState<'site' | 'desktop'>('site')
  const [code, setCode] = useState<string | null>(null)
  const [expiresAt, setExpiresAt] = useState<string | null>(null)
  const [generating, setGenerating] = useState(false)
  const [copied, setCopied] = useState(false)
  const [agentConnected, setAgentConnected] = useState(false)
  const { toast } = useToast()

  const generateCode = useCallback(async () => {
    setGenerating(true)
    try {
      const result = await api.post<{ code: string; expires_at: string }>(`/sites/${siteId}/agent/generate-code`)
      setCode(result.code)
      setExpiresAt(result.expires_at)
    } catch {
      toast('Failed to generate activation code', 'error')
    }
    setGenerating(false)
  }, [siteId, toast])

  useEffect(() => {
    generateCode()
  }, [generateCode])

  // Poll for agent connection
  useEffect(() => {
    if (!code || agentConnected) return
    const interval = setInterval(async () => {
      try {
        const status = await api.get<{ configured: boolean }>(`/sites/${siteId}/agent/status`)
        if (status.configured) {
          setAgentConnected(true)
          toast('Agent connected successfully!', 'success')
          onInstalled()
        }
      } catch { /* ignore */ }
    }, 5000)
    return () => clearInterval(interval)
  }, [code, siteId, agentConnected, toast, onInstalled])

  const copyCode = () => {
    if (code) {
      navigator.clipboard.writeText(code)
      setCopied(true)
      toast('Code copied', 'success')
      setTimeout(() => setCopied(false), 2000)
    }
  }

  const timeLeft = expiresAt ? Math.max(0, Math.floor((new Date(expiresAt).getTime() - Date.now()) / 1000)) : 0
  const hoursLeft = Math.floor(timeLeft / 3600)
  const minsLeft = Math.floor((timeLeft % 3600) / 60)

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)',
    }} onClick={onClose}>
      <div
        style={{
          background: 'var(--bg-1)', borderRadius: 'var(--r-xl)', border: '1px solid var(--border)',
          width: 480, maxWidth: '90vw', padding: 32, position: 'relative',
          boxShadow: 'var(--sh-lg)',
        }}
        onClick={e => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          style={{
            position: 'absolute', top: 16, right: 16, background: 'none',
            border: 'none', cursor: 'pointer', color: 'var(--text-3)', padding: 4,
          }}
        >
          <XIcon size={18} />
        </button>

        <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-1)', marginBottom: 12 }}>
          Install Agent
        </div>

        {/* Agent Type Toggle */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 16 }}>
          <button
            onClick={() => { setAgentType('site'); setCode(null); }}
            style={{
              flex: 1, padding: '10px 12px', borderRadius: 'var(--r-m)',
              border: agentType === 'site' ? '1px solid var(--accent)' : '1px solid var(--border)',
              background: agentType === 'site' ? 'var(--accent-m)' : 'var(--bg-2)',
              cursor: 'pointer', textAlign: 'left',
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 600, color: agentType === 'site' ? 'var(--accent)' : 'var(--text-1)' }}>
              Site Agent
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 2 }}>
              Network proxy, device discovery, health monitoring
            </div>
          </button>
          <button
            onClick={() => { setAgentType('desktop'); setCode(null); }}
            style={{
              flex: 1, padding: '10px 12px', borderRadius: 'var(--r-m)',
              border: agentType === 'desktop' ? '1px solid var(--accent)' : '1px solid var(--border)',
              background: agentType === 'desktop' ? 'var(--accent-m)' : 'var(--bg-2)',
              cursor: 'pointer', textAlign: 'left',
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 600, color: agentType === 'desktop' ? 'var(--accent)' : 'var(--text-1)' }}>
              Desktop Agent
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 2 }}>
              Remote desktop, screen sharing, keyboard/mouse control
            </div>
          </button>
        </div>

        <p style={{ fontSize: 13, color: 'var(--text-2)', lineHeight: 1.6, margin: '0 0 20px' }}>
          {agentType === 'site'
            ? 'Download and run the agent on a Windows or Linux machine at the client site. Enter the activation code when prompted.'
            : 'Download and run the desktop agent on the Windows computer you want to access remotely. It will appear as a device in the portal.'}
        </p>

        {agentConnected ? (
          <div style={{
            background: 'rgba(34,197,94,0.1)', border: '1px solid var(--ok)',
            borderRadius: 'var(--r-l)', padding: 20, textAlign: 'center', marginBottom: 24,
          }}>
            <CheckIcon size={32} style={{ color: 'var(--ok)', marginBottom: 8 }} />
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--ok)' }}>Agent Connected!</div>
            <div style={{ fontSize: 13, color: 'var(--text-2)', marginTop: 4 }}>
              The site agent is now online and ready.
            </div>
          </div>
        ) : code ? (
          <>
            {/* Activation Code Display */}
            <div style={{
              background: 'var(--bg-2)', borderRadius: 'var(--r-l)', padding: 20,
              textAlign: 'center', marginBottom: 20, border: '1px solid var(--border)',
            }}>
              <div style={{ fontSize: 11, color: 'var(--text-3)', marginBottom: 8, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Activation Code
              </div>
              <div style={{
                fontSize: 32, fontWeight: 800, fontFamily: 'var(--mono)',
                color: 'var(--accent)', letterSpacing: 8, lineHeight: 1,
              }}>
                {code}
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 8 }}>
                Expires in {hoursLeft}h {minsLeft}m
              </div>
              <button
                onClick={copyCode}
                style={{
                  marginTop: 12, padding: '6px 14px', borderRadius: 'var(--r-m)',
                  border: '1px solid var(--border)', background: 'var(--bg-1)',
                  fontSize: 12, fontWeight: 500, cursor: 'pointer', color: 'var(--text-1)',
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                }}
              >
                {copied ? <CheckIcon size={13} style={{ color: 'var(--ok)' }} /> : <CopyIcon size={13} />}
                {copied ? 'Copied' : 'Copy Code'}
              </button>
            </div>

            {/* Download Buttons */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
              <Button
                variant="secondary"
                size="sm"
                icon={<DownloadIcon />}
                style={{ flex: 1 }}
                onClick={() => window.open(`/api/agent/download/${agentType === 'desktop' ? 'desktop-windows' : 'windows'}`, '_blank')}
              >
                Windows .exe
              </Button>
              <Button
                variant="secondary"
                size="sm"
                icon={<DownloadIcon />}
                style={{ flex: 1 }}
                onClick={() => window.open(`/api/agent/download/${agentType === 'desktop' ? 'desktop-linux' : 'linux'}`, '_blank')}
              >
                Linux
              </Button>
            </div>

            {/* Steps */}
            <ol style={{
              fontSize: 13, color: 'var(--text-2)', paddingLeft: 20,
              lineHeight: 2, margin: 0,
            }}>
              <li>Download the agent for the target platform</li>
              <li>Run the installer on a machine at the client site</li>
              <li>Enter the activation code: <strong style={{ fontFamily: 'var(--mono)', color: 'var(--accent)' }}>{code}</strong></li>
              <li>The agent will connect automatically</li>
            </ol>

            <div style={{
              marginTop: 16, padding: '10px 12px', borderRadius: 'var(--r-m)',
              background: 'var(--bg-2)', fontSize: 12, color: 'var(--text-3)',
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
              <div style={{
                width: 8, height: 8, borderRadius: '50%', background: 'var(--accent)',
                animation: 'pulse 2s infinite',
              }} />
              Waiting for agent to connect...
            </div>
          </>
        ) : (
          <div style={{ textAlign: 'center', padding: 20 }}>
            <Button onClick={generateCode} loading={generating}>Generate Code</Button>
          </div>
        )}
      </div>
    </div>
  )
}
